/** Display timezone for all UI dates. Storage and API stay UTC. */
export const TEHRAN_TZ = 'Asia/Tehran';

/**
 * Parse an API/DB instant as UTC.
 * Strings without `Z` / offset are treated as UTC (not browser-local).
 */
export function parseUtcDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  const trimmed = value.trim();
  if (!trimmed) return new Date(Number.NaN);
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) return new Date(trimmed);
  const normalized = trimmed.includes('T')
    ? trimmed
    : trimmed.replace(' ', 'T');
  return new Date(`${normalized}Z`);
}

/** ISO UTC string for query/body when talking to the backend. */
export function toUtcIso(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : parseUtcDate(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** Absolute calendar/time for UI (Asia/Tehran). */
export function formatTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '—';
  const date = parseUtcDate(value);
  if (Number.isNaN(date.getTime())) return '—';

  const parts = new Intl.DateTimeFormat('fa-IR', {
    timeZone: TEHRAN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  }).formatToParts(date);

  const day = parts.find((part) => part.type === 'day')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const hour = parts.find((part) => part.type === 'hour')?.value;
  const minute = parts.find((part) => part.type === 'minute')?.value;

  // Assemble explicitly so BiDi cannot split «۳۰ مرداد» around the time.
  if (day && month && hour != null && minute != null) {
    const nbsp = '\u00A0';
    return `${day}${nbsp}${month}،${nbsp}${hour}:${minute}`;
  }

  return new Intl.DateTimeFormat('fa-IR', {
    timeZone: TEHRAN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/** Relative label for UI (based on absolute UTC instant). */
export function formatRelativeFa(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === '') return '—';
  const date = parseUtcDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'همین حالا';
  if (minutes < 60) return `${minutes} دقیقه پیش`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ساعت پیش`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'دیروز';
  return `${days} روز پیش`;
}

/** Start of calendar day in Tehran as a UTC Date (for range filters). */
export function startOfTehranDay(now = new Date()): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TEHRAN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !d) return new Date(Number.NaN);
  // Iran has no DST; Tehran is UTC+03:30 year-round.
  return new Date(`${y}-${m}-${d}T00:00:00+03:30`);
}
